import React, { useState, useEffect, useRef, useCallback } from 'react';
import lumiObserver from '../lumiObserver';

// ── HSC-depth projectile: hit a target ring using parametric equations
// x(t) = v·cos(θ)·t,   y(t) = v·sin(θ)·t − ½·g·t²
// The student must land within the target window on the ground. Unlike
// the generic projectile primitive, this one shows the parametric
// equations live and requires a precise hit, not just a "find 45°".
// ────────────────────────────────────────────────────────────────────────

const T = {
  bg: '#0D1117', surface: '#161B22', border: '#30363D',
  text: '#E6EDF3', textMid: '#8B949E',
  accent: '#0B8A7E', correct: '#10B981', wrong: '#EF4444', hint: '#F59E0B',
};

// Target window (metres from launch). HSC-style "hit a building roof".
const TARGET = { x: 75, halfWidth: 3, y: 18, heightTolerance: 3 };
const g = 9.8;

export default function ProjectileHSC({ interactiveId = 'projectile_hsc_p1' }) {
  const [angle, setAngle] = useState(30);
  const [velocity, setVelocity] = useState(28);
  const [fired, setFired] = useState(false);
  const [outcome, setOutcome] = useState(null); // 'hit' | 'miss' | null
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startRef = useRef(0);

  const rad = angle * Math.PI / 180;
  const vx = velocity * Math.cos(rad);
  const vy = velocity * Math.sin(rad);
  const flight = (2 * vy) / g;
  const range = (velocity * velocity * Math.sin(2 * rad)) / g;

  const W = 520, H = 260, groundY = H - 30, launchX = 30;
  const scale = (W - launchX - 20) / 100; // fixed: 0 to 100 m across

  const emit = (type, payload) => lumiObserver.emit({ interactiveId, type, payload });

  useEffect(() => {
    emit('aim_adjusted', { angle, velocity, predictedRange: Number(range.toFixed(2)) });
  // eslint-disable-next-line
  }, [angle, velocity]);

  const draw = useCallback((tSim) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    // Ground
    ctx.fillStyle = T.surface;
    ctx.fillRect(0, groundY, W, H - groundY);

    // Distance markers every 10 m
    ctx.strokeStyle = T.border; ctx.fillStyle = T.textMid;
    ctx.font = '10px sans-serif'; ctx.textAlign = 'center';
    for (let d = 10; d <= 100; d += 10) {
      const x = launchX + d * scale;
      ctx.beginPath(); ctx.moveTo(x, groundY); ctx.lineTo(x, groundY - 4); ctx.stroke();
      ctx.fillText(`${d}m`, x, groundY + 14);
    }

    // Target ring (platform at y=TARGET.y, width 2*halfWidth)
    const tx = launchX + TARGET.x * scale;
    const ty = groundY - TARGET.y * scale;
    ctx.fillStyle = T.hint + '44';
    ctx.fillRect(tx - TARGET.halfWidth * scale, ty - 4, 2 * TARGET.halfWidth * scale, 8);
    ctx.strokeStyle = T.hint; ctx.lineWidth = 2;
    ctx.strokeRect(tx - TARGET.halfWidth * scale, ty - 4, 2 * TARGET.halfWidth * scale, 8);
    ctx.fillStyle = T.hint; ctx.fillText('target', tx, ty - 8);

    // Cannon
    ctx.save();
    ctx.translate(launchX, groundY);
    ctx.rotate(-rad);
    ctx.fillStyle = T.accent;
    ctx.fillRect(0, -6, 30, 12);
    ctx.restore();

    // Predicted path
    ctx.strokeStyle = T.accent + '66'; ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let k = 0; k <= 60; k++) {
      const t = (k / 60) * flight;
      const px = launchX + vx * t * scale;
      const py = groundY - (vy * t - 0.5 * g * t * t) * scale;
      if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.stroke();

    // Live projectile
    if (tSim != null) {
      const t = Math.min(tSim, flight);
      const px = launchX + vx * t * scale;
      const py = groundY - (vy * t - 0.5 * g * t * t) * scale;
      ctx.fillStyle = T.hint;
      ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill();
    }
  }, [angle, velocity, flight, rad, scale, vx, vy]);

  useEffect(() => { draw(null); }, [draw]);

  const loop = (ts) => {
    if (!startRef.current) startRef.current = ts;
    const t = (ts - startRef.current) / 1000;
    draw(t);
    if (t < flight) {
      animRef.current = requestAnimationFrame(loop);
    } else {
      // Decide hit vs miss at the target x-position
      const tAtTarget = TARGET.x / vx;
      const yAtTarget = vy * tAtTarget - 0.5 * g * tAtTarget * tAtTarget;
      const hit = Math.abs(tAtTarget - flight / 2) < 99 /* always consider */
        && Math.abs(yAtTarget - TARGET.y) <= TARGET.heightTolerance
        && vx > 0;
      setOutcome(hit ? 'hit' : 'miss');
      emit(hit ? 'target_hit' : 'target_missed', {
        angle, velocity,
        yAtTarget: Number(yAtTarget.toFixed(2)),
        range: Number(range.toFixed(2)),
      });
      setFired(false); startRef.current = 0;
    }
  };

  const fire = () => {
    if (fired) return;
    setOutcome(null); setFired(true); startRef.current = 0;
    emit('fired', { angle, velocity });
    animRef.current = requestAnimationFrame(loop);
  };

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ fontSize: 11, color: T.textMid, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>Physics · HSC Module 5 · Projectile Motion</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>Projectile — Hit the target</div>
      <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>
        Target platform at x = {TARGET.x} m, y = {TARGET.y} m (±{TARGET.heightTolerance} m).
        Use the parametric equations to choose angle and velocity.
      </div>

      <canvas ref={canvasRef} width={W} height={H}
        style={{ display: 'block', width: '100%', maxWidth: W, margin: '12px auto', background: T.bg, borderRadius: 8 }}
        aria-label={`Projectile simulation at ${angle}° and ${velocity} m/s`} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: T.textMid }}>
            <span>Angle θ</span><span style={{ color: T.accent }}>{angle}°</span>
          </div>
          <input type="range" min={10} max={80} step={1} value={angle} onChange={e => setAngle(Number(e.target.value))} style={{ width: '100%', accentColor: T.accent }} aria-label="Launch angle" />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: T.textMid, marginTop: 6 }}>
            <span>Velocity v</span><span style={{ color: T.accent }}>{velocity} m/s</span>
          </div>
          <input type="range" min={15} max={45} step={1} value={velocity} onChange={e => setVelocity(Number(e.target.value))} style={{ width: '100%', accentColor: T.accent }} aria-label="Launch velocity" />
        </div>
        <div style={{ background: T.bg, padding: 10, borderRadius: 8, fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ color: T.textMid, fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>PARAMETRIC EQUATIONS</div>
          <div><span style={{ color: T.textMid }}>x(t) =</span> {vx.toFixed(1)} · t</div>
          <div><span style={{ color: T.textMid }}>y(t) =</span> {vy.toFixed(1)} · t − 4.9 · t²</div>
          <div style={{ marginTop: 6, color: T.textMid }}>
            At target (x = {TARGET.x} m): t = {(TARGET.x / Math.max(vx, 0.01)).toFixed(2)} s,
            y = {(vy * (TARGET.x / Math.max(vx, 0.01)) - 0.5 * g * Math.pow(TARGET.x / Math.max(vx, 0.01), 2)).toFixed(1)} m
          </div>
        </div>
      </div>

      <button onClick={fire} disabled={fired}
        style={{ width: '100%', marginTop: 10, padding: 10, background: fired ? T.border : T.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: fired ? 'not-allowed' : 'pointer' }}>
        {fired ? 'Firing…' : '🚀 Fire'}
      </button>

      {outcome === 'hit' && (
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: T.correct + '22', border: `1px solid ${T.correct}`, color: T.correct, fontSize: 13, fontWeight: 700 }}>
          ✓ Target hit. Landed at y = {(vy * (TARGET.x / vx) - 0.5 * g * Math.pow(TARGET.x / vx, 2)).toFixed(1)} m.
        </div>
      )}
      {outcome === 'miss' && (
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: T.wrong + '22', border: `1px solid ${T.wrong}`, color: T.wrong, fontSize: 13, fontWeight: 700 }}>
          ✗ Missed. At x = {TARGET.x} m, y came out to {(vy * (TARGET.x / Math.max(vx, 0.01)) - 0.5 * g * Math.pow(TARGET.x / Math.max(vx, 0.01), 2)).toFixed(1)} m.
        </div>
      )}
    </div>
  );
}
